// src/components/Contact.jsx
import React, { useMemo, useState } from "react";
import "../styles/Contact.css";
import { apiUrl } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    website: "", // honeypot (hidden)
  });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const canSubmit = useMemo(() => {
    return (
      !busy &&
      form.website === "" && // honeypot must stay empty
      form.name.trim() &&
      form.email.trim() &&
      form.message.trim()
      // subject is OPTIONAL (server accepts blank)
    );
  }, [busy, form]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setErr("");
    setOk(false);

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      subject: form.subject.trim(), // optional
      message: form.message.trim(),
      website: form.website, // include honeypot
    };

    // If honeypot is filled (bot), silently succeed
    if (form.website && form.website.trim() !== "") {
      setOk(true);
      setBusy(false);
      return;
    }

    // 12s safety timeout
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort("timeout"), 12000);

    try {
      const res = await fetch(apiUrl("/api/contact"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
        signal: ctrl.signal,
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        // non-JSON error body
      }

      if (!res.ok || data?.ok === false) {
        const msg =
          data?.error ||
          data?.details ||
          data?.mc_body ||
          `HTTP ${res.status}`;
        throw new Error(String(msg));
      }

      setOk(true);
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (e) {
      const reason =
        e?.name === "AbortError" ? "Request timed out" : e?.message || "Failed to send";
      setErr(reason);
    } finally {
      clearTimeout(to);
      setBusy(false);
    }
  }

  return (
    <section className="contact" aria-labelledby="contact-title">
      <h1 id="contact-title">Contact</h1>
      <p className="contact__lead">
        Send us a message and we’ll get back to you.
      </p>

      {/* Status messages with aria-live for screen readers */}
      <div aria-live="polite" aria-atomic="true">
        {ok && (
          <div className="contact__alert contact__alert--ok" role="status">
            Thanks! Your message has been sent.
          </div>
        )}
        {err && (
          <div className="contact__alert contact__alert--err" role="alert">
            Error: {err}
          </div>
        )}
      </div>

      <form className="contact__form" onSubmit={onSubmit} noValidate>
        {/* Honeypot (visually hidden via CSS .hp) */}
        <input
          type="text"
          name="website"
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value })}
          autoComplete="off"
          className="hp"
          tabIndex="-1"
          aria-hidden="true"
        />

        <label>
          <span>Name</span>
          <input
            type="text"
            required
            autoComplete="name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </label>

        <label>
          <span>Email</span>
          <input
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </label>

        <label>
          <span>
            Subject <em className="optional">(optional)</em>
          </span>
          <input
            type="text"
            placeholder="e.g., Book inquiry"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
          />
        </label>

        <label>
          <span>Message</span>
          <textarea
            rows={6}
            required
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
        </label>

        <button className="contact__btn" type="submit" disabled={!canSubmit}>
          {busy ? "Sending…" : "Send Message"}
        </button>
      </form>
    </section>
  );
}
